import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Shield, Search, Edit, Trash2, Lock, Unlock, ChevronRight, 
  User, MapPin, Briefcase, Phone, Mail, Home, Save, X,
  Users, Settings, Eye, EyeOff, AlertTriangle, KeyRound, ShieldAlert, Plus, ChevronDown, ChevronUp, Copy, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';
import * as perms from '../lib/permissions';

// Feature toggle definitions - organized by page
const FEATURE_TOGGLES = [
  // Home/Dashboard
  { id: 'home_entire', label: 'Home/Dashboard Entire Page', category: 'Home' },
  { id: 'home_key_metrics', label: 'Home/Dashboard - Key Metrics', category: 'Home' },
  { id: 'home_key_priorities', label: 'Home/Dashboard - Key Priorities', category: 'Home' },
  { id: 'home_key_trackers', label: 'Home/Dashboard - Key Trackers', category: 'Home' },
  
  // Calls
  { id: 'calls_entire', label: 'Calls - Entire Page', category: 'Calls' },
  { id: 'calls_keypad', label: 'Calls - Calls Keypad tab', category: 'Calls' },
  { id: 'calls_keypad_recording', label: 'Calls - Keypad Call recording only', category: 'Calls' },
  { id: 'calls_keypad_ai_summary', label: 'Calls - Keypad AI Summary and Transcript only', category: 'Calls' },
  { id: 'calls_history', label: 'Calls - Call History tab', category: 'Calls' },
  { id: 'calls_contacts', label: 'Calls - Contacts icon (synced with Messages)', category: 'Calls' },
  
  // Chat
  { id: 'chat_entire', label: 'Chat - Entire page + AI personality filtering + chat history', category: 'Chat' },
  
  // Calendar
  { id: 'calendar_entire', label: 'Calendar - Entire page', category: 'Calendar' },
  { id: 'calendar_appointments', label: 'Calendar - My appointments tab', category: 'Calendar' },
  { id: 'calendar_my_calendar', label: 'Calendar - My calendar tab', category: 'Calendar' },
  
  // Tasks
  { id: 'tasks_entire', label: 'Tasks - Entire page', category: 'Tasks' },
  { id: 'tasks_personal', label: 'Tasks - Personal tasks', category: 'Tasks' },
  { id: 'tasks_assigned', label: 'Tasks - Assigned to me', category: 'Tasks' },
  { id: 'tasks_completed', label: 'Tasks - Completed tasks', category: 'Tasks' },
  
  // Resources
  { id: 'resources_my', label: 'Resources - My resources', category: 'Resources' },
  { id: 'resources_company', label: 'Resources - Company resources', category: 'Resources' },
  
  // Users & Teams
  { id: 'search_users_entire', label: 'Search Users entire page', category: 'Users' },
  { id: 'my_team_entire', label: 'My Team entire page', category: 'Users' },
  { id: 'my_team_onboarded', label: 'My Team - Onboarded tab', category: 'Users' },
  { id: 'my_team_pending', label: 'My Team - Pending tab', category: 'Users' },
  { id: 'my_team_rejected', label: 'My Team - Rejected tab', category: 'Users' },
  { id: 'my_peers_entire', label: 'My Peers - Entire page', category: 'Users' },
  
  // ORB Characters
  { id: 'orb_characters_entire', label: 'ORB Characters - Entire page', category: 'ORB Characters' },
  { id: 'orb_ai_assistant', label: 'ORB Characters - ORB AI Assistant', category: 'ORB Characters' },
  { id: 'orb_mena_entire', label: "ORB Characters - Mena's entire profile", category: 'ORB Characters' },
  { id: 'orb_mena_about', label: "ORB Characters - Mena's About Me", category: 'ORB Characters' },
  { id: 'orb_mena_releases', label: "ORB Characters - Mena's New Releases", category: 'ORB Characters' },
  { id: 'orb_mena_videos', label: "ORB Characters - Mena's My Videos", category: 'ORB Characters' },
  { id: 'orb_mena_music', label: "ORB Characters - Mena's My Music", category: 'ORB Characters' },
  { id: 'orb_mena_stories', label: "ORB Characters - Mena's My Stories", category: 'ORB Characters' },
  { id: 'orb_katie_entire', label: "ORB Characters - Katie's entire profile", category: 'ORB Characters' },
  { id: 'orb_katie_releases', label: "ORB Characters - Katie's New Releases", category: 'ORB Characters' },
  { id: 'orb_katie_videos', label: "ORB Characters - Katie's My Videos", category: 'ORB Characters' },
  { id: 'orb_katie_music', label: "ORB Characters - Katie's My Music", category: 'ORB Characters' },
  { id: 'orb_katie_stories', label: "ORB Characters - Katie's My Stories", category: 'ORB Characters' },
  { id: 'orb_luke_entire', label: "ORB Characters - Luke's entire profile", category: 'ORB Characters' },
  { id: 'orb_luke_releases', label: "ORB Characters - Luke's New Releases", category: 'ORB Characters' },
  { id: 'orb_luke_videos', label: "ORB Characters - Luke's My Videos", category: 'ORB Characters' },
  { id: 'orb_luke_music', label: "ORB Characters - Luke's My Music", category: 'ORB Characters' },
  { id: 'orb_luke_stories', label: "ORB Characters - Luke's My Stories", category: 'ORB Characters' },
  { id: 'orb_dorian_entire', label: "ORB Characters - Dorian's entire profile", category: 'ORB Characters' },
  { id: 'orb_dorian_releases', label: "ORB Characters - Dorian's New Releases", category: 'ORB Characters' },
  { id: 'orb_dorian_videos', label: "ORB Characters - Dorian's My Videos", category: 'ORB Characters' },
  { id: 'orb_dorian_music', label: "ORB Characters - Dorian's My Music", category: 'ORB Characters' },
  { id: 'orb_dorian_stories', label: "ORB Characters - Dorian's My Stories", category: 'ORB Characters' },
  { id: 'orb_octavio_entire', label: "ORB Characters - Octavio's entire profile", category: 'ORB Characters' },
  { id: 'orb_octavio_releases', label: "ORB Characters - Octavio's New Releases", category: 'ORB Characters' },
  { id: 'orb_octavio_videos', label: "ORB Characters - Octavio's My Videos", category: 'ORB Characters' },
  { id: 'orb_octavio_music', label: "ORB Characters - Octavio's My Music", category: 'ORB Characters' },
  { id: 'orb_octavio_stories', label: "ORB Characters - Octavio's My Stories", category: 'ORB Characters' },
  
  // Settings
  { id: 'settings_appearance', label: 'Settings - Appearance light/dark mode toggle', category: 'Settings' },
  { id: 'settings_welcome_rewatch', label: 'Settings - Welcome page re-watch', category: 'Settings' },
  
  // Messages
  { id: 'messages_entire', label: 'Messages - Entire page', category: 'Messages' },
  { id: 'messages_internal', label: 'Messages - Internal chat section', category: 'Messages' },
  { id: 'messages_external', label: 'Messages - External texts section', category: 'Messages' },
  { id: 'messages_contacts', label: 'Messages - Contacts button (synced with Calls)', category: 'Messages' },
  
  // Profile Restrictions
  { id: 'profile_lock_name', label: 'View Profile - Lock Essential details (name)', category: 'Profile', defaultEnabled: true },
  { id: 'profile_lock_state', label: 'View Profile - Lock State', category: 'Profile', defaultEnabled: true },
  { id: 'profile_lock_industry', label: 'View Profile - Lock Industry', category: 'Profile', defaultEnabled: true },
  { id: 'profile_lock_domain', label: 'View Profile - Lock Domain (follows Industry)', category: 'Profile', defaultEnabled: false },
];

// Admin-exclusive features. These are OFF by default for every user —
// only admins (role === 'admin') or users whose admin-feature toggle is
// explicitly ON can see/access the corresponding page/tab. Used by the
// Create User dialog so the admin can pre-grant specific admin powers
// to a non-admin user without promoting them to admin.
const ADMIN_FEATURE_TOGGLES = [
  // Admin management
  { id: 'admin_user_administration', label: 'User Administration (/admin/users)', category: 'Admin' },
  { id: 'admin_onboarding', label: 'Onboarding Management (/admin/onboarding)', category: 'Admin' },
  { id: 'admin_assignments', label: 'Assignments (/admin/assignments)', category: 'Admin' },
  { id: 'admin_security_requirements', label: 'Security Requirements (/admin/security-requirements)', category: 'Admin' },

  // Communications & routing
  { id: 'admin_user_communications', label: 'User Communications (/admin/user-communications)', category: 'Communications' },
  { id: 'admin_calling', label: 'Calling Admin (/admin/calling)', category: 'Communications' },
  { id: 'admin_phone_routing_standard', label: 'Phone Routing — Standard States', category: 'Communications' },
  { id: 'admin_phone_routing_exemption', label: 'Phone Routing — Exemptions', category: 'Communications' },
  { id: 'admin_phone_routing_restricted', label: 'Phone Routing — Restricted States', category: 'Communications' },
  { id: 'admin_email_monitor', label: 'Email — Monitor tab (all state threads)', category: 'Communications' },
  { id: 'admin_email_queue', label: 'Email — Admin Queue tab (unmatched inbound)', category: 'Communications' },

  // States & geography
  { id: 'admin_statewide_details', label: 'Statewide Details (/admin/statewide-details)', category: 'States' },
  { id: 'admin_restricted_states', label: 'Restricted States (/admin/restricted-states)', category: 'States' },
  { id: 'admin_orb_universes', label: 'ORB Universes (/admin/orb-universes)', category: 'States' },

  // Contacts oversight
  { id: 'admin_master_contacts', label: 'Master Contacts (/admin/master-contacts)', category: 'Contacts' },
  { id: 'admin_master_history', label: 'Master History (/admin/master-history)', category: 'Contacts' },

  // AI / Voice
  { id: 'admin_ai_training', label: 'AI Training Hub (/admin/ai-training)', category: 'AI' },
  { id: 'admin_ai_voice_agent', label: 'Voice Agent Training (/admin/ai-training/voice-agent)', category: 'AI' },
  { id: 'admin_ai_voice_dashboard', label: 'Voice Agent Dashboard (/admin/ai-voice-dashboard)', category: 'AI' },
];

const UserAdminPage = () => {
  const navigate = useNavigate();
  const { api, user: currentUser } = useAuth();
  // iter108 — Approve-override deep-link. When NotificationsPage
  // routes admin here with `?override_request_id=<id>`, we fetch the
  // request, auto-open the requester's edit dialog → Security tab,
  // and pass the request context down to BypassSection so it can
  // prefill the factor checkboxes + invoke the approve endpoint after
  // a successful Issue Bypass.
  const [overrideRequestCtx, setOverrideRequestCtx] = useState(null);
  const [editDialogTab, setEditDialogTab] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState(null); // null = all, or 'admin', 'supervisor', 'lead', 'affiliate'
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showFeaturesDialog, setShowFeaturesDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    first_name: '', last_name: '', email: '', role: 'supervisor', state: '',
    industry: '', industry_domain: '', personal_phone: '', personal_email: '',
    home_address: '', orb_phone: '', orb_email: '', orb_address: '',
    permissions: {},
  });
  const [createSaving, setCreateSaving] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  // Per-card expansion state for the inline "Admin Feature Access"
  // editor on Special-User rows. Map keyed by user id.
  const [expandedSpecial, setExpandedSpecial] = useState({});
  const [savingSpecialId, setSavingSpecialId] = useState(null);

  const isAdmin = currentUser?.role === 'admin';
  // Permission helpers — special users with the `admin_user_administration`
  // toggle get scoped access, full admins get everything.
  const userAdminPerms = perms.userAdmin(currentUser);
  const canAccessPage = userAdminPerms.view;

  // Mobile header — title + Security Requirements shield + create-user Plus action.
  usePageHeader({
    title: 'User Administration',
    rightSlot: (
      <div className="flex items-center gap-1">
        {currentUser?.role === 'admin' && (
          <button
            onClick={() => navigate('/admin/security-requirements')}
            className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
            aria-label="Security Requirements"
            data-testid="header-security-req-btn"
          >
            <Shield className="w-5 h-5" />
          </button>
        )}
        {userAdminPerms.createUser && (
          <button
            onClick={() => setShowCreateDialog(true)}
            className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2a] flex items-center justify-center"
            aria-label="Create user"
            data-testid="header-create-user-btn"
          >
            <Plus className="w-5 h-5" />
          </button>
        )}
      </div>
    ),
  }, [userAdminPerms.createUser, setShowCreateDialog, currentUser?.role, navigate]);
  // Registered state→ORB phone mappings, used to populate the ORB Phone dropdown.
  const [registeredOrbNumbers, setRegisteredOrbNumbers] = useState([]);
  
  // iter170 — Secure deletion state.
  // Steps:
  //   1 = password    | 4 = delete code
  //   2 = recovery    | 5 = confirm
  //   3 = TOTP        | 6 = attest
  const [deleteStep, setDeleteStep] = useState(1);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteRecoveryKey, setDeleteRecoveryKey] = useState('');
  const [deleteTotpCode, setDeleteTotpCode] = useState('');
  const [deleteCode, setDeleteCode] = useState('');
  const [attestationChecked, setAttestationChecked] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Security tab — admin must re-auth (password + TOTP) before the tab
  // content renders. The unlock state is scoped to the currently-open
  // Edit User dialog and is reset whenever it closes or the admin
  // navigates between tabs (see SecurityTab + onValueChange below).
  const [securityUnlocked, setSecurityUnlocked] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/users/team');
      setAllUsers(res.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canAccessPage) {
      loadUsers();
      // Load registered state numbers once so the ORB Phone dropdown is ready
      api.get('/twilio/phone-numbers/registered')
        .then((res) => setRegisteredOrbNumbers(res.data?.items || []))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // iter108 — pull `?override_request_id=<id>` and auto-open the
  // target user's Security tab.
  useEffect(() => {
    if (!canAccessPage || allUsers.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const ovrId = params.get('override_request_id');
    if (!ovrId || overrideRequestCtx?.id === ovrId) return;
    api.get(`/admin/override-requests/${ovrId}`).then((r) => {
      const ovr = r.data;
      if (!ovr) return;
      setOverrideRequestCtx(ovr);
      const target = allUsers.find((u) => u.id === ovr.requester_user_id);
      if (target) {
        setSelectedUser(target);
        setShowEditDialog(true);
        setEditDialogTab('security');
      } else {
        toast.error('User from the override request was not found in this admin view');
      }
    }).catch(() => toast.error('Could not load override request'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, canAccessPage]);

  // Only admin can access this page
  if (!canAccessPage) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24">
        <div className="max-w-2xl mx-auto pt-4 flex flex-col items-center justify-center min-h-[400px]">
          <Shield className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground text-center">Only administrators can access this page.</p>
          <Button className="mt-4" onClick={() => navigate('/')}>Return Home</Button>
        </div>
      </div>
    );
  }

  const getInitials = (u) => {
    if (u?.first_name && u?.last_name) {
      return `${u.first_name[0]}${u.last_name[0]}`.toUpperCase();
    }
    if (u?.full_name) {
      return u.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  const getFullName = (u) => {
    if (u?.first_name && u?.last_name) {
      return `${u.first_name} ${u.last_name}`;
    }
    return u?.full_name || 'Unknown User';
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin': return 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20';
      case 'supervisor': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
      case 'lead': return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
      default: return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20';
    }
  };

  const getRoleGradient = (role) => {
    switch (role) {
      case 'admin': return 'from-rose-500 to-pink-600';
      case 'supervisor': return 'from-blue-500 to-indigo-600';
      case 'lead': return 'from-emerald-500 to-teal-600';
      default: return 'from-slate-500 to-slate-600';
    }
  };

  const handleEditUser = (u) => {
    setSelectedUser(u);
    setEditForm({
      first_name: u.first_name || '',
      last_name: u.last_name || '',
      email: u.email || '',
      role: u.role || 'affiliate',
      // iter 121 — synthetic boolean mirroring `created_by_admin`. The
      // backend stores the acting-admin's id (truthy) or null. We
      // surface it to the FE as a clean bool so the Security-tab role
      // dropdown can flip the user between "regular" and "special".
      is_special_user: !!u.created_by_admin,
      title: u.title || '',
      industry: u.industry || '',
      industry_domain: u.industry_domain || '',
      state: u.state || '',
      personal_phone: u.personal_phone || '',
      orb_phone: u.orb_phone || '',
      orb_email: u.orb_email || '',
      home_address: u.home_address || '',
      orb_address: u.orb_address || '',
      stocks_awarded: u.stocks_awarded || 0,
      onboard_count: u.onboard_count || 0,
      referral_count: u.referral_count || 0,
      uplift_score: u.uplift_score || 0,
      is_locked: u.is_locked || false,
      // Admin feature toggles live on `feature_toggles`. Clone so
      // edits inside the dialog don't mutate the row in `allUsers`.
      feature_toggles: { ...(u.feature_toggles || {}) },
    });
    setEditDialogTab('basic');
    setShowEditDialog(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    
    try {
      await api.put(`/admin/users/${selectedUser.id}`, editForm);
      toast.success('User updated successfully');
      setShowEditDialog(false);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const resetDeleteState = () => {
    setDeleteStep(1);
    setDeletePassword('');
    setDeleteRecoveryKey('');
    setDeleteTotpCode('');
    setDeleteCode('');
    setAttestationChecked(false);
    setDeleteError('');
    setDeleteLoading(false);
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setSelectedUser(null);
    resetDeleteState();
  };

  const handleVerifyCredentials = () => {
    // iter170 — moved to per-step validation inline in the dialog.
    // Kept as a no-op shim in case anything else still references it.
    setDeleteError('');
    setDeleteStep(5);
  };

  const handleConfirmDelete = () => {
    // iter170 — confirm screen leads into attestation.
    setDeleteStep(6);
  };

  const handleFinalDelete = async () => {
    if (!selectedUser) return;
    if (!attestationChecked) {
      setDeleteError('You must confirm the attestation to proceed');
      return;
    }
    
    setDeleteLoading(true);
    setDeleteError('');
    
    try {
      await api.post(`/admin/users/${selectedUser.id}/secure-delete`, {
        password: deletePassword,
        recovery_key: deleteRecoveryKey,
        totp_code: deleteTotpCode,
        delete_code: deleteCode,
        attestation_confirmed: true
      });
      toast.success('User permanently deleted');
      handleDeleteDialogClose();
      loadUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to delete user';
      setDeleteError(errorMsg);
      // iter170 — jump back to the failing credential's step so the
      // admin can correct it in place. Final POST is at step 6; any
      // 401 / 400 from the backend names which input was wrong.
      if (errorMsg.includes('password') || errorMsg.includes('Password')) {
        setDeleteStep(1);
      } else if (errorMsg.includes('recovery key') || errorMsg.includes('Recovery')) {
        setDeleteStep(2);
      } else if (errorMsg.includes('authenticator') || errorMsg.includes('TOTP')) {
        setDeleteStep(3);
      } else if (errorMsg.includes('delete code') || errorMsg.includes('Delete')) {
        setDeleteStep(4);
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleLock = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { is_locked: !u.is_locked });
      toast.success(u.is_locked ? 'User unlocked' : 'User locked');
      loadUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleManageFeatures = (u) => {
    setSelectedUser(u);
    setShowFeaturesDialog(true);
  };

  const handleToggleFeature = async (featureId, enabled) => {
    if (!selectedUser) return;
    
    try {
      const featureToggles = { ...(selectedUser.feature_toggles || {}), [featureId]: enabled };
      
      // Sync contacts toggle between calls and messages
      if (featureId === 'calls_contacts') {
        featureToggles['messages_contacts'] = enabled;
      } else if (featureId === 'messages_contacts') {
        featureToggles['calls_contacts'] = enabled;
      }
      
      // Sync domain lock with industry lock
      if (featureId === 'profile_lock_industry' && enabled) {
        featureToggles['profile_lock_domain'] = true;
      }
      
      await api.put(`/admin/users/${selectedUser.id}`, { feature_toggles: featureToggles });
      
      // Update selectedUser state
      setSelectedUser({ ...selectedUser, feature_toggles: featureToggles });
      
      // ALSO update the allUsers array so when dialog is re-opened, it has the latest data
      setAllUsers(prevUsers => prevUsers.map(u => 
        u.id === selectedUser.id 
          ? { ...u, feature_toggles: featureToggles }
          : u
      ));
      
      toast.success('Feature updated');
    } catch (error) {
      toast.error('Failed to update feature');
    }
  };

  const filteredUsers = allUsers.filter(u => {
    const name = getFullName(u).toLowerCase();
    const email = (u.email || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    const matchesSearch = name.includes(query) || email.includes(query);
    let matchesRole;
    if (roleFilter === null) {
      matchesRole = true;
    } else if (roleFilter === 'special') {
      // "Special Users" = anyone provisioned via the admin Create-User
      // dialog (those rows carry `created_by_admin`). Independent of
      // role so an admin-created affiliate, lead, supervisor, etc.
      // all show up here.
      matchesRole = !!u.created_by_admin;
    } else {
      // Role buckets exclude admin-created users so the role count
      // shown on the stat card matches the list of users displayed
      // when that role is selected. Admin-created users live solely
      // in the "Special" bucket — see the count logic above.
      matchesRole = u.role === roleFilter && !u.created_by_admin;
    }
    return matchesSearch && matchesRole;
  });

  const groupedFeatures = FEATURE_TOGGLES.reduce((acc, feature) => {
    if (!acc[feature.category]) acc[feature.category] = [];
    acc[feature.category].push(feature);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="user-admin-page">
      <div className="max-w-4xl mx-auto pt-4">
        {/* Search */}
        <Card className="bg-white/90 dark:bg-[#141414] border-0 shadow-lg mb-4">
          <CardContent className="p-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Stats - Clickable Role Filters (admin/supervisor/lead/affiliate + Special).
            Role buckets EXCLUDE admin-created users so each user is counted in
            exactly one card — `Admins + Supervisors + Leads + Affiliates + Special`
            equals the total user count. Without this guard an admin-created
            supervisor (e.g. jbp@collectiveorb.com) was counted in both
            "Supervisors" and "Special", inflating the stat-card sum by exactly
            the number of admin-created users and making the page look like
            certain users were missing from other admin views. */}
        <div className="grid grid-cols-5 gap-2 mb-4">
          {['affiliate', 'lead', 'supervisor', 'special', 'admin'].map((role) => {
            const isSelected = roleFilter === role;
            const count = role === 'special'
              ? allUsers.filter(u => !!u.created_by_admin).length
              : allUsers.filter(u => u.role === role && !u.created_by_admin).length;
            const label = role === 'special' ? 'Special' : `${role}s`;
            return (
              <Card
                key={role}
                className={`bg-white/90 dark:bg-[#141414] border-0 shadow-md cursor-pointer transition-all ${
                  isSelected ? 'ring-2 ring-[#586D52] ring-offset-2' : 'hover:shadow-lg'
                }`}
                onClick={() => setRoleFilter(isSelected ? null : role)}
                data-testid={`filter-${role}`}
              >
                <CardContent className="p-3 text-center">
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs capitalize text-muted-foreground">{label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Active Filter Indicator */}
        {roleFilter && (
          <div className="flex items-center gap-2 mb-3">
            <Badge className="capitalize text-white bg-[#586D52]">
              Showing: {roleFilter === 'special' ? 'Special Users' : `${roleFilter}s`}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setRoleFilter(null)}
            >
              Clear filter
            </Button>
          </div>
        )}

        {/* User List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin mb-3" />
            <span className="text-slate-500 text-sm">Loading users...</span>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground px-1">{filteredUsers.length} users</p>
            {filteredUsers.map((u) => (
              <Card 
                key={u.id}
                className={`bg-white/90 dark:bg-[#141414] border-0 shadow-md ${u.is_locked ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12 border-2 border-white dark:border-[#1a1a1a] shadow-md">
                      <AvatarImage src={u.profile_picture} />
                      <AvatarFallback className={`bg-gradient-to-br ${getRoleGradient(u.role)} text-white`}>
                        {getInitials(u)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold truncate">{getFullName(u)}</p>
                        {u.is_locked && <Lock className="w-3 h-3 text-red-500" />}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={`text-[10px] capitalize px-1.5 py-0 border ${getRoleBadgeStyle(u.role)}`}>
                          {u.title || u.role}
                        </Badge>
                        {u.state && <span className="text-[10px] text-muted-foreground">{u.state}</span>}
                        <Badge variant={u.is_onboarded ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0">
                          {u.is_onboarded ? 'Onboarded' : 'Pending'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => handleEditUser(u)}
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleToggleLock(u)}
                        disabled={!userAdminPerms.lockTarget(u)}
                        data-testid={`lock-user-${u.id}`}
                      >
                        {u.is_locked ? <Unlock className="w-4 h-4 text-green-500" /> : <Lock className="w-4 h-4 text-amber-500" />}
                      </Button>
                      {userAdminPerms.alterFeatureAccess && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleManageFeatures(u)}
                        data-testid={`features-user-${u.id}`}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      )}
                      {userAdminPerms.deleteUser && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:text-red-600"
                        onClick={() => { setSelectedUser(u); setShowDeleteDialog(true); }}
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      )}
                    </div>
                  </div>

                  {/* Special-User admin-feature inline editor — only
                      a full admin can grant/revoke admin-feature
                      access. Special users can VIEW but not edit. */}
                  {u.created_by_admin && userAdminPerms.alterFeatureAccess && (
                    <div className="mt-2 pt-2 border-t border-rose-100 dark:border-rose-900/30">
                      <button
                        onClick={() => setExpandedSpecial((prev) => ({ ...prev, [u.id]: !prev[u.id] }))}
                        className="w-full flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400 hover:opacity-80 transition-opacity"
                        data-testid={`special-toggle-${u.id}`}
                      >
                        <span className="flex items-center gap-1.5">
                          <ShieldAlert className="w-3.5 h-3.5" />
                          Admin Feature Access
                          <Badge variant="outline" className="text-[10px] font-semibold border-rose-300 text-rose-700 dark:text-rose-400">
                            {Object.values(u.feature_toggles || {}).filter(Boolean).length} granted
                          </Badge>
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${expandedSpecial[u.id] ? 'rotate-180' : ''}`} />
                      </button>
                      {expandedSpecial[u.id] && (
                        <div className="mt-2 rounded-lg border border-rose-200 dark:border-rose-900/40 bg-rose-50/40 dark:bg-rose-900/10 p-2.5 space-y-2.5" data-testid={`special-perms-${u.id}`}>
                          {(() => {
                            const groups = {};
                            for (const f of ADMIN_FEATURE_TOGGLES) {
                              if (!groups[f.category]) groups[f.category] = [];
                              groups[f.category].push(f);
                            }
                            return Object.entries(groups).map(([cat, feats]) => (
                              <div key={cat}>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400 mb-1">{cat}</p>
                                <div className="space-y-1">
                                  {feats.map((f) => (
                                    <div key={f.id} className="flex items-center gap-2 text-xs">
                                      <Switch
                                        checked={!!(u.feature_toggles || {})[f.id]}
                                        onCheckedChange={async (v) => {
                                          setSavingSpecialId(u.id);
                                          try {
                                            const nextToggles = { ...(u.feature_toggles || {}), [f.id]: v };
                                            await api.put(`/admin/users/${u.id}`, { feature_toggles: nextToggles });
                                            setAllUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, feature_toggles: nextToggles } : x));
                                            toast.success(`${v ? 'Granted' : 'Revoked'} ${f.label}`);
                                          } catch (err) {
                                            toast.error('Failed to update access');
                                          } finally {
                                            setSavingSpecialId(null);
                                          }
                                        }}
                                        disabled={savingSpecialId === u.id}
                                        data-testid={`special-toggle-${u.id}-${f.id}`}
                                      />
                                      <span className="leading-tight">{f.label}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ));
                          })()}
                          {/* iter127 — Inline per-state Email Inbox access editor */}
                          <StateInboxAccessEditor
                            mode="edit"
                            userId={u.id}
                            defaultState={(u.state || '').toUpperCase()}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(o) => { setShowEditDialog(o); if (!o) setSecurityUnlocked(false); }}>
        <DialogContent className="max-w-lg w-[calc(100%-2rem)] max-h-[90vh] overflow-y-auto bg-white dark:bg-[#1a1a1a] rounded-3xl p-0">
          <DialogHeader className="p-4 pb-2 sticky top-0 bg-white dark:bg-[#1a1a1a] z-10">
            <DialogTitle className="text-lg font-bold">Edit User</DialogTitle>
            <p className="text-xs text-muted-foreground">{selectedUser?.email}</p>
          </DialogHeader>
          
          <div className="px-4 pb-4 space-y-4">
            <Tabs value={editDialogTab} onValueChange={(v) => { setEditDialogTab(v); if (v !== 'security') setSecurityUnlocked(false); }} className="w-full">
              <TabsList className={`w-full grid ${userAdminPerms.editMetrics ? 'grid-cols-4' : 'grid-cols-3'}`}>
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="contact">Contact</TabsTrigger>
                {userAdminPerms.editMetrics && (
                  <TabsTrigger value="metrics">Metrics</TabsTrigger>
                )}
                <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">First Name</Label>
                    <Input 
                      value={editForm.first_name} 
                      onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Last Name</Label>
                    <Input 
                      value={editForm.last_name} 
                      onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input 
                    value={editForm.email} 
                    onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Role</Label>
                    <div
                      className="w-full mt-1 h-9 rounded-md border bg-muted/40 px-3 text-sm flex items-center capitalize text-muted-foreground"
                      data-testid="user-edit-role-readonly"
                    >
                      {editForm.is_special_user
                        ? 'Supervisor (Special User)'
                        : (editForm.role || '—')}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Read-only here. Change role under the <strong>Security</strong> tab.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Custom Title</Label>
                    <div
                      className="w-full mt-1 h-9 rounded-md border bg-muted/40 px-3 text-sm flex items-center text-muted-foreground"
                      data-testid="user-edit-title-readonly"
                    >
                      {editForm.title || <span className="italic">— none —</span>}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Edit in the <strong>Security</strong> tab.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">State</Label>
                    <Input 
                      value={editForm.state} 
                      onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Industry</Label>
                    <Input 
                      value={editForm.industry} 
                      onChange={(e) => setEditForm({...editForm, industry: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Industry Domain</Label>
                  <Input 
                    value={editForm.industry_domain} 
                    onChange={(e) => setEditForm({...editForm, industry_domain: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="contact" className="space-y-3 mt-3">
                <div>
                  <Label className="text-xs">Personal Phone</Label>
                  <Input 
                    value={editForm.personal_phone} 
                    onChange={(e) => setEditForm({...editForm, personal_phone: e.target.value})}
                    className="mt-1"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <Label className="text-xs">ORB Phone</Label>
                  <select
                    value={editForm.orb_phone || ''}
                    onChange={(e) => setEditForm({ ...editForm, orb_phone: e.target.value })}
                    className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="edit-orb-phone-select"
                  >
                    <option value="">— Not assigned —</option>
                    {registeredOrbNumbers.map((it) => (
                      <option key={it.state} value={it.phone_number} data-testid={`orb-phone-option-${it.state}`}>
                        {it.state} — {it.phone_number}
                      </option>
                    ))}
                    {/* Preserve a custom (non-registered) value the user already has. */}
                    {editForm.orb_phone && !registeredOrbNumbers.some((it) => it.phone_number === editForm.orb_phone) && (
                      <option value={editForm.orb_phone}>
                        Custom — {editForm.orb_phone}
                      </option>
                    )}
                  </select>
                  {registeredOrbNumbers.length === 0 && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      No state numbers registered yet. Add some in Calling → Phone Numbers.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Home Address</Label>
                  <Input 
                    value={editForm.home_address} 
                    onChange={(e) => setEditForm({...editForm, home_address: e.target.value})}
                    className="mt-1"
                    placeholder="123 Main St, City, State ZIP"
                  />
                </div>
                {/* iter 97 — Verified Identity (read-only, admin view).
                    Only renders when the user has gone through the
                    barcode-scan flow. Admin can override by editing
                    first_name / last_name above, but the ID-derived
                    fields themselves are always sourced from the
                    physical scan (no manual entry). */}
                {selectedUser?.id_verified_at && (
                  <div className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/10 p-3" data-testid="admin-edit-id-card">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider">Verified Identity (from State ID)</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-muted-foreground">First (ID):</span> <span className="font-medium">{selectedUser.id_first_name || '—'}</span></div>
                      <div><span className="text-muted-foreground">Last (ID):</span> <span className="font-medium">{selectedUser.id_last_name || '—'}</span></div>
                      {selectedUser.id_middle_name ? (
                        <div className="col-span-2"><span className="text-muted-foreground">Middle:</span> <span className="font-medium">{selectedUser.id_middle_name}</span></div>
                      ) : null}
                      <div><span className="text-muted-foreground">DOB:</span> <span className="font-medium">{selectedUser.id_dob || '—'}</span></div>
                      <div><span className="text-muted-foreground">ID State:</span> <span className="font-medium">{selectedUser.id_state || '—'}</span></div>
                      <div className="col-span-2"><span className="text-muted-foreground">Address (ID):</span> <span className="font-medium">{[selectedUser.id_address, selectedUser.id_city, selectedUser.id_state, selectedUser.id_postal_code].filter(Boolean).join(', ') || '—'}</span></div>
                      <div><span className="text-muted-foreground">License #:</span> <span className="font-medium">{selectedUser.id_license_number || '—'}</span></div>
                      <div><span className="text-muted-foreground">Expires:</span> <span className="font-medium">{selectedUser.id_expiry || '—'}</span></div>
                      <div className="col-span-2"><span className="text-muted-foreground">Verified:</span> <span className="font-medium">{new Date(selectedUser.id_verified_at).toLocaleString()}</span></div>
                    </div>
                  </div>
                )}
                {/* Show preferred name section only when set */}
                {selectedUser?.preferred_first_name && (
                  <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/10 p-3" data-testid="admin-edit-preferred-name">
                    <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wider mb-1">Preferred First Name</p>
                    <p className="text-sm font-medium">{selectedUser.preferred_first_name}</p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Shown on the user's business card / search results in place of their legal first name.
                    </p>
                  </div>
                )}
                <div>
                  <Label className="text-xs">ORB Address</Label>
                  <select
                    value={editForm.orb_address || ''}
                    onChange={(e) => setEditForm({ ...editForm, orb_address: e.target.value })}
                    className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="edit-orb-address-select"
                  >
                    <option value="">— Not assigned —</option>
                    {registeredOrbNumbers.filter((it) => it.orb_address).map((it) => (
                      <option key={it.state} value={it.orb_address}>
                        {it.state} — {it.orb_address}
                      </option>
                    ))}
                    {editForm.orb_address && !registeredOrbNumbers.some((it) => it.orb_address === editForm.orb_address) && (
                      <option value={editForm.orb_address}>Custom — {editForm.orb_address}</option>
                    )}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">ORB Email</Label>
                  <select
                    value={editForm.orb_email || ''}
                    onChange={(e) => setEditForm({ ...editForm, orb_email: e.target.value })}
                    className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="edit-orb-email-select"
                  >
                    <option value="">— Not assigned —</option>
                    {registeredOrbNumbers.filter((it) => it.orb_email).map((it) => (
                      <option key={it.state} value={it.orb_email}>
                        {it.state} — {it.orb_email}
                      </option>
                    ))}
                    {editForm.orb_email && !registeredOrbNumbers.some((it) => it.orb_email === editForm.orb_email) && (
                      <option value={editForm.orb_email}>Custom — {editForm.orb_email}</option>
                    )}
                  </select>
                </div>
              </TabsContent>
              
              <TabsContent value="metrics" className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Total Equity Awarded</Label>
                    <Input 
                      type="number"
                      value={editForm.stocks_awarded} 
                      onChange={(e) => setEditForm({...editForm, stocks_awarded: Number(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Onboard Count</Label>
                    <Input 
                      type="number"
                      value={editForm.onboard_count} 
                      onChange={(e) => setEditForm({...editForm, onboard_count: Number(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Referral Count</Label>
                    <Input 
                      type="number"
                      value={editForm.referral_count} 
                      onChange={(e) => setEditForm({...editForm, referral_count: Number(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Uplift Score</Label>
                    <Input 
                      type="number"
                      value={editForm.uplift_score} 
                      onChange={(e) => setEditForm({...editForm, uplift_score: Number(e.target.value)})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-red-700 dark:text-red-400">Account Locked</p>
                    <p className="text-xs text-red-600 dark:text-red-500">User cannot login when locked</p>
                  </div>
                  <Switch 
                    checked={editForm.is_locked}
                    onCheckedChange={(checked) => setEditForm({...editForm, is_locked: checked})}
                  />
                </div>
              </TabsContent>

              <TabsContent value="security" className="space-y-3 mt-3">
                {currentUser?.role === 'admin' && selectedUser ? (
                  <>
                    {/* Role / Permission editor is gated behind the
                        same re-auth wall as the rest of the Security
                        tab. Renders ONLY after the admin re-verifies
                        their password (and TOTP when applicable). */}
                    {securityUnlocked && (
                      <RolePermissionEditor
                        editForm={editForm}
                        setEditForm={setEditForm}
                        selectedUser={selectedUser}
                      />
                    )}
                    <SecurityTab
                      api={api}
                      targetUser={selectedUser}
                      isSelf={selectedUser.id === currentUser?.id}
                      unlocked={securityUnlocked}
                      onUnlock={() => setSecurityUnlocked(true)}
                      overrideRequestCtx={overrideRequestCtx}
                    />
                  </>
                ) : (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-800 dark:text-amber-300" data-testid="security-tab-admin-only">
                    Security controls are restricted to administrators.
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-[#2C3E50] hover:bg-[#34495E]" onClick={handleSaveUser} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {/* Secure Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={handleDeleteDialogClose}>
        <DialogContent className="max-w-md w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl">
          <DialogHeader className="p-4 pb-2 border-b border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20">
            <DialogTitle className="text-lg font-bold text-red-600 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" />
              {deleteStep === 1 && 'Step 1 of 4 — Password'}
              {deleteStep === 2 && 'Step 2 of 4 — Recovery Key'}
              {deleteStep === 3 && 'Step 3 of 4 — Authenticator Code'}
              {deleteStep === 4 && 'Step 4 of 4 — Delete Code'}
              {deleteStep === 5 && 'Confirm Deletion'}
              {deleteStep === 6 && 'Final Attestation'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="px-4 pb-4 space-y-4">
            {/* User being deleted info */}
            <div className="flex items-center gap-3 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-medium text-sm">{selectedUser && getFullName(selectedUser)}</p>
                <p className="text-xs text-muted-foreground">{selectedUser?.email}</p>
              </div>
            </div>

            {/* iter170 — Step 1: Password */}
            {deleteStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Enter your admin password to continue.
                </p>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Your Password
                  </Label>
                  <Input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Enter your admin password"
                    className="bg-slate-50 dark:bg-slate-800"
                    data-testid="delete-password-input"
                    autoComplete="current-password"
                    autoFocus
                  />
                </div>
                {deleteError && (
                  <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded" data-testid="delete-error">{deleteError}</p>
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={handleDeleteDialogClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => { setDeleteError(''); setDeleteStep(2); }}
                    disabled={!deletePassword}
                    data-testid="delete-step1-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* iter170 — Step 2: Recovery Key */}
            {deleteStep === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Enter your account recovery key.
                </p>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1">
                    <KeyRound className="w-3 h-3" /> Recovery Key
                  </Label>
                  <Input
                    type="text"
                    value={deleteRecoveryKey}
                    onChange={(e) => setDeleteRecoveryKey(e.target.value)}
                    placeholder="Enter your account recovery key"
                    className="bg-slate-50 dark:bg-slate-800 font-mono"
                    data-testid="delete-recovery-input"
                    autoComplete="off"
                    autoFocus
                  />
                </div>
                {deleteError && (
                  <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded" data-testid="delete-error">{deleteError}</p>
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setDeleteError(''); setDeleteStep(1); }}>
                    Back
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => { setDeleteError(''); setDeleteStep(3); }}
                    disabled={!deleteRecoveryKey}
                    data-testid="delete-step2-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* iter170 — Step 3: Authenticator code */}
            {deleteStep === 3 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Enter the 6-digit code from your authenticator app.
                </p>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Authenticator Code
                  </Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={deleteTotpCode}
                    onChange={(e) => setDeleteTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength={6}
                    className="bg-slate-50 dark:bg-slate-800 font-mono tracking-widest text-center text-lg"
                    data-testid="delete-totp-input"
                    autoComplete="one-time-code"
                    autoFocus
                  />
                </div>
                {deleteError && (
                  <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded" data-testid="delete-error">{deleteError}</p>
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setDeleteError(''); setDeleteStep(2); }}>
                    Back
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => { setDeleteError(''); setDeleteStep(4); }}
                    disabled={deleteTotpCode.length !== 6}
                    data-testid="delete-step3-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}

            {/* iter170 — Step 4: Delete code */}
            {deleteStep === 4 && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Enter the secure delete code.
                </p>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete Code
                  </Label>
                  <Input
                    type="password"
                    value={deleteCode}
                    onChange={(e) => setDeleteCode(e.target.value)}
                    placeholder="Enter the delete code"
                    className="bg-slate-50 dark:bg-slate-800 font-mono"
                    data-testid="delete-code-input"
                    autoComplete="off"
                    autoFocus
                  />
                </div>
                {deleteError && (
                  <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded" data-testid="delete-error">{deleteError}</p>
                )}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setDeleteError(''); setDeleteStep(3); }}>
                    Back
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => { setDeleteError(''); setDeleteStep(5); }}
                    disabled={!deleteCode}
                    data-testid="delete-step4-next"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Step 5: Confirm Deletion */}
            {deleteStep === 5 && (
              <div className="space-y-4">
                <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2">
                    WARNING: This action CANNOT be undone!
                  </p>
                  <p className="text-sm text-red-600 dark:text-red-300">
                    You are about to permanently delete the account for <strong>{selectedUser && getFullName(selectedUser)}</strong>. 
                    All of the following will be permanently removed:
                  </p>
                  <ul className="mt-2 text-xs text-red-600 dark:text-red-300 space-y-1 list-disc list-inside">
                    <li>User profile and all personal data</li>
                    <li>All messages and conversations</li>
                    <li>All tasks and assignments</li>
                    <li>All contacts created by this user</li>
                  </ul>
                </div>

                <p className="text-sm text-center font-medium">
                  Are you absolutely sure you want to proceed?
                </p>

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setDeleteStep(4)}>
                    Go Back
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={handleConfirmDelete}
                    data-testid="delete-step5-confirm"
                  >
                    Yes, I'm Sure
                  </Button>
                </div>
              </div>
            )}

            {/* Step 6: Final Attestation */}
            {deleteStep === 6 && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-100 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 mb-2">
                    Corporate Authority Attestation
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-300">
                    By proceeding with this deletion, you confirm that you have reviewed all relevant policies 
                    and have the appropriate corporate authority to permanently delete this user account.
                  </p>
                </div>

                <div className="flex items-start gap-3 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <Checkbox 
                    id="attestation"
                    checked={attestationChecked}
                    onCheckedChange={setAttestationChecked}
                    className="mt-0.5"
                    data-testid="attestation-checkbox"
                  />
                  <label htmlFor="attestation" className="text-sm leading-relaxed cursor-pointer">
                    I attest that I have the corporate authority to delete this user account and understand 
                    that this action is permanent and cannot be reversed. I accept full responsibility for this action.
                  </label>
                </div>

                {deleteError && (
                  <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 p-2 rounded" data-testid="delete-error">{deleteError}</p>
                )}

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => setDeleteStep(5)}>
                    Go Back
                  </Button>
                  <Button 
                    variant="destructive" 
                    className="flex-1"
                    onClick={handleFinalDelete}
                    disabled={!attestationChecked || deleteLoading}
                    data-testid="delete-step6-final"
                  >
                    {deleteLoading ? 'Deleting...' : 'Permanently Delete User'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Feature Toggles Dialog */}
      <Dialog open={showFeaturesDialog} onOpenChange={setShowFeaturesDialog}>
        <DialogContent className="max-w-lg w-[calc(100%-2rem)] max-h-[90vh] overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl p-0">
          <DialogHeader className="p-4 pb-2 sticky top-0 bg-white dark:bg-[#1a1a1a] z-10 border-b">
            <DialogTitle className="text-lg font-bold">Feature Access</DialogTitle>
            <p className="text-xs text-muted-foreground">{selectedUser && getFullName(selectedUser)}</p>
          </DialogHeader>
          
          <ScrollArea className="max-h-[70vh] px-4 pb-4">
            {Object.entries(groupedFeatures).map(([category, features]) => (
              <div key={category} className="mb-4">
                <h3 className="text-sm font-semibold text-muted-foreground mb-2 sticky top-0 bg-white dark:bg-[#1a1a1a] py-1">
                  {category}
                </h3>
                <div className="space-y-1">
                  {features.map((feature) => {
                    const isEnabled = selectedUser?.feature_toggles?.[feature.id] ?? !feature.defaultEnabled;
                    return (
                      <div 
                        key={feature.id}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
                      >
                        <span className="text-sm">{feature.label}</span>
                        <Switch 
                          checked={isEnabled}
                          onCheckedChange={(checked) => handleToggleFeature(feature.id, checked)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(o) => { setShowCreateDialog(o); if (!o) setCreatedPassword(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription>Create a new user as if they had signed up themselves.</DialogDescription>
          </DialogHeader>
          {createdPassword ? (
            <div className="space-y-3">
              <div className="rounded-lg p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200">
                <p className="text-sm font-semibold mb-1">User created — temporary password (save now)</p>
                <p className="text-xs text-muted-foreground mb-2">This password is shown only once. Copy it and share securely.</p>
                <code className="font-mono text-base bg-white dark:bg-[#0d0d0d] px-3 py-2 rounded block break-all">{createdPassword}</code>
              </div>
              <Button onClick={() => { navigator.clipboard.writeText(createdPassword); toast.success('Copied'); }}>Copy</Button>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Done</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">First name</Label><Input value={createForm.first_name} onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Last name</Label><Input value={createForm.last_name} onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })} className="mt-1" /></div>
                <div className="col-span-2"><Label className="text-xs">Email</Label><Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} className="mt-1" /></div>
                <div className="col-span-2 grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Permission Level</Label>
                    <select
                      value={
                        ['admin','supervisor'].includes((createForm.role || '').toLowerCase())
                          ? (createForm.role || '').toLowerCase()
                          : 'supervisor'
                      }
                      onChange={(e) => {
                        setCreateForm({ ...createForm, role: e.target.value });
                      }}
                      className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      data-testid="create-user-permission-level"
                    >
                      <option value="supervisor">Supervisor (Special User)</option>
                      <option value="admin">Admin (FULL EQUAL ACCESS)</option>
                    </select>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Manual creation is the fallback for adding <strong>leaders</strong> to the platform.
                      Regular Affiliates / Leads / Supervisors come through public signup.
                      <strong> Admin</strong> creates a full-fledged admin equal to you.
                      <strong> Supervisor (Special User)</strong> creates a staff account &mdash; toggle the admin-feature boxes below to grant page-by-page rights.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">Custom Title <span className="text-muted-foreground">(optional)</span></Label>
                    <Input
                      value={createForm.custom_title || ''}
                      onChange={(e) => setCreateForm({ ...createForm, custom_title: e.target.value })}
                      placeholder='e.g. "Director of Operations"'
                      className="mt-1"
                      data-testid="create-user-custom-title"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Display label only. Doesn&apos;t change permissions.
                    </p>
                  </div>
                </div>
                <div><Label className="text-xs">State</Label><Input value={createForm.state} onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })} className="mt-1" placeholder="NC" /></div>
                <div><Label className="text-xs">Industry</Label><Input value={createForm.industry} onChange={(e) => setCreateForm({ ...createForm, industry: e.target.value })} className="mt-1" /></div>
                <div className="col-span-2"><Label className="text-xs">Industry domain</Label><Input value={createForm.industry_domain} onChange={(e) => setCreateForm({ ...createForm, industry_domain: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Personal phone</Label><Input value={createForm.personal_phone} onChange={(e) => setCreateForm({ ...createForm, personal_phone: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Personal email</Label><Input type="email" value={createForm.personal_email} onChange={(e) => setCreateForm({ ...createForm, personal_email: e.target.value })} className="mt-1" /></div>
                <div className="col-span-2"><Label className="text-xs">Home address</Label><Input value={createForm.home_address} onChange={(e) => setCreateForm({ ...createForm, home_address: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">ORB Phone</Label><Input value={createForm.orb_phone} onChange={(e) => setCreateForm({ ...createForm, orb_phone: e.target.value })} className="mt-1" placeholder="auto-fills from state" /></div>
                <div><Label className="text-xs">ORB Email</Label><Input value={createForm.orb_email} onChange={(e) => setCreateForm({ ...createForm, orb_email: e.target.value })} className="mt-1" placeholder="auto-fills from state" /></div>
                <div className="col-span-2"><Label className="text-xs">ORB Address</Label><Input value={createForm.orb_address} onChange={(e) => setCreateForm({ ...createForm, orb_address: e.target.value })} className="mt-1" placeholder="auto-fills from state" /></div>
              </div>

              {/* Admin Feature Permissions — visually distinct, all OFF by default */}
              {(() => {
                const isSpecial = (createForm.role || '').toLowerCase() !== 'admin';
                if (!isSpecial) return null;
                return (
                  <>
                    <div className="rounded-xl border-2 border-dashed border-rose-300 dark:border-rose-700 bg-rose-50/40 dark:bg-rose-900/10 p-3 mt-2" data-testid="create-user-admin-perms">
                      <p className="text-xs font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300 flex items-center gap-1.5"><ShieldAlert className="w-3.5 h-3.5" /> Admin Feature Access</p>
                      <p className="text-[11px] text-muted-foreground mb-2">
                        These are ADMIN-ONLY pages &amp; tabs. The new user will have
                        their role&apos;s standard features by default; toggle any
                        admin feature ON to grant access to that specific admin area.
                      </p>
                      {(() => {
                        const groups = {};
                        for (const f of ADMIN_FEATURE_TOGGLES) {
                          if (!groups[f.category]) groups[f.category] = [];
                          groups[f.category].push(f);
                        }
                        return Object.entries(groups).map(([cat, feats]) => (
                          <div key={cat} className="mt-2 first:mt-0">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400 mb-1">{cat}</p>
                            <div className="space-y-1">
                              {feats.map((f) => (
                                <div key={f.id} className="flex items-center gap-2 text-xs">
                                  <Switch
                                    checked={!!createForm.permissions[f.id]}
                                    onCheckedChange={(v) => setCreateForm({ ...createForm, permissions: { ...createForm.permissions, [f.id]: v } })}
                                    data-testid={`create-user-admin-${f.id}`}
                                  />
                                  <span>{f.label}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                    {/* iter127 — per-state Email Inbox access for Special Users */}
                    <StateInboxAccessEditor
                      mode="create"
                      defaultState={(createForm.state || '').toUpperCase()}
                      value={createForm.inbox_memberships_map || {}}
                      onChange={(map) => setCreateForm({
                        ...createForm,
                        inbox_memberships_map: map,
                        inbox_memberships: Object.entries(map).map(([state, flags]) => ({ state, ...flags })),
                      })}
                    />
                  </>
                );
              })()}

              <div className="flex gap-2 pt-2">
                <Button
                  className="bg-[#586D52] hover:bg-[#4a5d46] flex-1"
                  disabled={createSaving || !createForm.first_name || !createForm.email}
                  onClick={async () => {
                    setCreateSaving(true);
                    try {
                      const res = await api.post('/admin/users/create', createForm);
                      if (res.data?.generated_password) setCreatedPassword(res.data.generated_password);
                      else { toast.success('User created'); setShowCreateDialog(false); }
                      // refresh list
                      try { await loadUsers(); } catch (e) {}
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Could not create user');
                    } finally {
                      setCreateSaving(false);
                    }
                  }}
                  data-testid="create-user-submit"
                >
                  <Save className="w-4 h-4 mr-1.5" />
                  {createSaving ? 'Creating…' : 'Create user'}
                </Button>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserAdminPage;

// =================== Role / Permission Editor ===================
// iter 121 — Sits at the top of the Security tab so role + custom
// title edits live alongside the rest of the high-impact admin
// controls. Basic Info shows the same values read-only.
//
// Dropdown options depend on the user's CURRENT classification:
//   • Already a Special User (`created_by_admin` truthy) → all 5
//     options visible (Affiliate, Lead, Supervisor, Supervisor
//     (Special User), Admin). Admin available ONLY through this
//     gateway — protects against an admin accidentally promoting a
//     stranger.
//   • Regular user → 4 options (Affiliate, Lead, Supervisor,
//     Supervisor (Special User)). "Admin" is hidden — the only way
//     to grant Admin is to first promote to Special.
//
// Selecting "Supervisor (Special User)" sets role=supervisor AND
// is_special_user=true. Selecting any base role clears
// is_special_user.
function RolePermissionEditor({ editForm, setEditForm, selectedUser }) {
  const currentValue = (() => {
    // Special-User flag takes precedence — anyone in the Special
    // bucket pre-selects "Supervisor (Special User)" regardless of
    // what their legacy stored role happens to be.
    if (editForm.is_special_user) return 'supervisor_special';
    return (editForm.role || '').toLowerCase();
  })();

  const isAlreadySpecial = !!selectedUser?.created_by_admin;
  const currentIsAdmin = (selectedUser?.role || '').toLowerCase() === 'admin';

  // Allowed options:
  //  - Always show: affiliate, lead, supervisor, supervisor_special
  //  - Show "admin" ONLY when the user is already special OR already admin
  const showAdminOption = isAlreadySpecial || currentIsAdmin;

  const handleChange = (value) => {
    if (value === 'supervisor_special') {
      setEditForm({ ...editForm, role: 'supervisor', is_special_user: true });
    } else {
      setEditForm({ ...editForm, role: value, is_special_user: false });
    }
  };

  const showAdminToggles = currentValue === 'supervisor_special';

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3" data-testid="security-role-editor">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Role &amp; Permission</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Permission Level</Label>
          <select
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full mt-1 h-9 rounded-md border bg-background px-3 text-sm"
            data-testid="user-edit-permission-level"
          >
            <option value="affiliate">Affiliate</option>
            <option value="lead">Lead</option>
            <option value="supervisor">Supervisor</option>
            <option value="supervisor_special">Supervisor (Special User)</option>
            {showAdminOption && (
              <option value="admin">Admin (FULL EQUAL ACCESS)</option>
            )}
          </select>
          {!showAdminOption && (
            <p className="text-[10px] text-muted-foreground mt-0.5">
              To promote to <strong>Admin</strong>, first set this user to <em>Supervisor (Special User)</em>, save, then re-open.
            </p>
          )}
        </div>
        <div>
          <Label className="text-xs">Custom Title <span className="text-muted-foreground">(optional)</span></Label>
          <Input
            value={editForm.title || ''}
            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
            className="mt-1"
            placeholder='e.g. "Director of Ops"'
            data-testid="user-edit-custom-title"
          />
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Display label only. Doesn&apos;t change permissions.
          </p>
        </div>
      </div>

      {showAdminToggles && (
        <div className="rounded-xl border-2 border-dashed border-rose-300 dark:border-rose-700 bg-rose-50/40 dark:bg-rose-900/10 p-3" data-testid="edit-user-admin-perms">
          <p className="text-xs font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300 flex items-center gap-1.5">
            <ShieldAlert className="w-3.5 h-3.5" /> Admin Feature Access
          </p>
          <p className="text-[11px] text-muted-foreground mb-2">
            These are ADMIN-ONLY pages &amp; tabs. Toggle ON to grant this Special User access to that
            specific admin area. Recovery-key regeneration still treats them as a <strong>Supervisor</strong>.
          </p>
          {(() => {
            const groups = {};
            for (const f of ADMIN_FEATURE_TOGGLES) {
              if (!groups[f.category]) groups[f.category] = [];
              groups[f.category].push(f);
            }
            return Object.entries(groups).map(([cat, feats]) => (
              <div key={cat} className="mt-2 first:mt-0">
                <p className="text-[11px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400 mb-1">{cat}</p>
                <div className="space-y-1">
                  {feats.map((f) => (
                    <div key={f.id} className="flex items-center gap-2 text-xs">
                      <Switch
                        checked={!!(editForm.feature_toggles || {})[f.id]}
                        onCheckedChange={(v) => setEditForm({
                          ...editForm,
                          feature_toggles: { ...(editForm.feature_toggles || {}), [f.id]: v },
                        })}
                        data-testid={`edit-user-admin-${f.id}`}
                      />
                      <span>{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>
      )}
      {/* iter127 — Per-state Email Inbox access editor for Special
          Users. Renders alongside the admin-feature toggles so the
          admin can grant cross-state inbox access independent of the
          page-level admin permissions. */}
      {showAdminToggles && selectedUser?.id && (
        <StateInboxAccessEditor
          mode="edit"
          userId={selectedUser.id}
          defaultState={(selectedUser.state || '').toUpperCase()}
        />
      )}
    </div>
  );
}


// =================== Email Inbox Access Editor ===================
// iter127 — Lets an admin grant a Special User access to state email
// inboxes outside of their default state. Three tiered toggles per
// state:
//   • Email box access — inbox + sent + drafts (can_send only)
//   • Queue access     — Email box + queue   (can_send + can_view_all)
//   • Monitor access   — Queue + monitor     (all three flags ON)
// Tiered: enabling a higher tier auto-enables lower tiers; disabling a
// lower tier auto-disables every tier above it.
//
// Two modes:
//   mode="edit"   — persists each change immediately via
//                   POST /email/admin/members (or DELETE when all 3
//                   toggles off).
//   mode="create" — manages an in-memory map and bubbles it up via
//                   onChange(memberships) so the parent Create-User
//                   submit payload can include it.
const TIER_RANK = { none: 0, send: 1, view: 2, monitor: 3 };
const computeTier = (g) => {
  if (g?.can_monitor) return 'monitor';
  if (g?.can_view_all) return 'view';
  if (g?.can_send) return 'send';
  return 'none';
};
const tierToFlags = (tier) => ({
  can_send: TIER_RANK[tier] >= 1,
  can_view_all: TIER_RANK[tier] >= 2,
  can_monitor: TIER_RANK[tier] >= 3,
});

function StateInboxAccessEditor({ mode, userId, defaultState, value, onChange }) {
  const { api } = useAuth();
  const [inboxes, setInboxes] = useState(null);  // [{state, email_address}]
  const [grants, setGrants] = useState({});      // state -> {can_send, can_view_all, can_monitor}
  const [savingState, setSavingState] = useState(null);

  // Load list of all configured state inboxes (admin sees every state).
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get('/email/inboxes');
        if (dead) return;
        setInboxes((data || []).map((i) => ({ state: (i.state || '').toUpperCase(), email_address: i.email_address })).sort((a, b) => a.state.localeCompare(b.state)));
      } catch (e) {
        if (!dead) setInboxes([]);
      }
    })();
    return () => { dead = true; };
  }, [api]);

  // Edit mode: load this user's existing memberships.
  useEffect(() => {
    if (mode !== 'edit' || !userId) return;
    let dead = false;
    (async () => {
      try {
        const { data } = await api.get(`/email/admin/user-memberships/${userId}`);
        if (dead) return;
        const map = {};
        for (const m of data || []) {
          const st = (m.state || '').toUpperCase();
          if (!st) continue;
          map[st] = {
            can_send: !!m.can_send,
            can_view_all: !!m.can_view_all,
            can_monitor: !!m.can_monitor,
          };
        }
        setGrants(map);
      } catch (e) {
        if (!dead) setGrants({});
      }
    })();
    return () => { dead = true; };
  }, [api, mode, userId]);

  // Create mode: receive grants from parent via controlled `value`.
  useEffect(() => {
    if (mode === 'create' && value) setGrants(value);
  }, [mode, value]);

  const applyTier = async (state, tier) => {
    const flags = tierToFlags(tier);
    const next = { ...grants };
    if (tier === 'none') delete next[state];
    else next[state] = flags;
    setGrants(next);
    if (mode === 'create') {
      onChange?.(next);
      return;
    }
    // Edit mode — persist immediately.
    setSavingState(state);
    try {
      if (tier === 'none') {
        await api.delete('/email/admin/members', { data: { state, user_id: userId } });
        toast.success(`${state} access revoked`);
      } else {
        await api.post('/email/admin/members', { state, user_id: userId, ...flags });
        const label = { send: 'Email box', view: 'Queue', monitor: 'Monitor' }[tier];
        toast.success(`${state} · ${label} access granted`);
      }
    } catch (e) {
      toast.error(`Failed to update ${state} access`);
      // Roll back optimistic state.
      const { data } = await api.get(`/email/admin/user-memberships/${userId}`).catch(() => ({ data: [] }));
      const rollback = {};
      for (const m of data || []) {
        rollback[(m.state || '').toUpperCase()] = {
          can_send: !!m.can_send, can_view_all: !!m.can_view_all, can_monitor: !!m.can_monitor,
        };
      }
      setGrants(rollback);
    } finally {
      setSavingState(null);
    }
  };

  if (inboxes === null) {
    return (
      <div className="mt-3 rounded-xl border-2 border-dashed border-sky-300 dark:border-sky-700 bg-sky-50/40 dark:bg-sky-900/10 p-3 text-xs text-muted-foreground">
        Loading state inboxes…
      </div>
    );
  }
  // Skip the user's default state — they always have full access there.
  const rows = inboxes.filter((i) => i.state !== (defaultState || '').toUpperCase());

  return (
    <div className="mt-3 rounded-xl border-2 border-dashed border-sky-300 dark:border-sky-700 bg-sky-50/40 dark:bg-sky-900/10 p-3" data-testid="state-inbox-access-editor">
      <p className="text-xs font-bold uppercase tracking-wider text-sky-700 dark:text-sky-300 flex items-center gap-1.5">
        <Mail className="w-3.5 h-3.5" /> Email Inboxes (cross-state access)
      </p>
      <p className="text-[11px] text-muted-foreground mb-2">
        Grant this Special User access to state mailboxes outside their default state ({defaultState || '—'}).
        Tiered toggles: <strong>Email box</strong> = inbox + sent + drafts ·
        <strong> Queue</strong> = adds queue tab ·
        <strong> Monitor</strong> = adds monitor tab. Higher tier auto-enables lower tiers.
      </p>
      {rows.length === 0 ? (
        <p className="text-[11px] text-muted-foreground italic">No additional state inboxes available.</p>
      ) : (
        <div className="space-y-1.5">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 px-1 text-[10px] font-bold uppercase tracking-wider text-sky-700 dark:text-sky-300">
            <span>State · Address</span>
            <span className="text-center w-16">Email box</span>
            <span className="text-center w-16">Queue</span>
            <span className="text-center w-16">Monitor</span>
          </div>
          {rows.map((i) => {
            const tier = computeTier(grants[i.state]);
            const checked = (level) => TIER_RANK[tier] >= TIER_RANK[level];
            const onToggle = (level, on) => {
              if (on) {
                applyTier(i.state, level);
              } else {
                // Turning OFF a level demotes to the next-lower level.
                const demoteTo = { send: 'none', view: 'send', monitor: 'view' }[level];
                applyTier(i.state, demoteTo);
              }
            };
            const busy = savingState === i.state;
            return (
              <div key={i.state} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center px-1 py-1 rounded-md hover:bg-sky-100/40 dark:hover:bg-sky-900/20" data-testid={`inbox-row-${i.state}`}>
                <div className="min-w-0">
                  <p className="text-xs font-semibold">{i.state}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{i.email_address}</p>
                </div>
                <div className="w-16 flex justify-center">
                  <Switch checked={checked('send')} disabled={busy} onCheckedChange={(v) => onToggle('send', v)} data-testid={`inbox-tier-send-${i.state}`} />
                </div>
                <div className="w-16 flex justify-center">
                  <Switch checked={checked('view')} disabled={busy} onCheckedChange={(v) => onToggle('view', v)} data-testid={`inbox-tier-view-${i.state}`} />
                </div>
                <div className="w-16 flex justify-center">
                  <Switch checked={checked('monitor')} disabled={busy} onCheckedChange={(v) => onToggle('monitor', v)} data-testid={`inbox-tier-monitor-${i.state}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// =================== Reset Password Section ===================
// Admin-only widget rendered at the bottom of the user-edit dialog.
// Generates a random password OR accepts a custom one. Reveals the new
// password ONCE so the admin can copy it; never re-displays existing or
// previously-set passwords (we only ever store the bcrypt hash).
function SecurityTab({ api, targetUser, isSelf, unlocked, onUnlock, overrideRequestCtx }) {
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [securityToken, setSecurityToken] = useState('');
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Tick once per second so the lockout countdown + security session
  // expiry render live without forcing the admin to refresh.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const verify = async () => {
    setVerifying(true);
    setVerifyError('');
    try {
      const body = { password };
      if (totpCode) body.totp_code = totpCode;
      const res = await api.post(`/admin/users/${targetUser.id}/security/verify`, body);
      setSecurityToken(res.data.security_token);
      setTokenExpiresAt(Date.now() + (res.data.expires_in_seconds || 300) * 1000);
      setPassword('');
      setTotpCode('');
      setNeedsTotp(false);
      onUnlock();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Verification failed';
      if (detail === 'TOTP_REQUIRED') {
        setNeedsTotp(true);
        setVerifyError('Enter your authenticator code to continue.');
      } else {
        setVerifyError(detail);
      }
    } finally {
      setVerifying(false);
    }
  };

  const loadOverview = async () => {
    if (!securityToken) return;
    setOverviewLoading(true);
    try {
      const res = await api.get(`/admin/users/${targetUser.id}/security/overview`, { params: { token: securityToken } });
      setOverview(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to load security data';
      toast.error(detail);
      if (e?.response?.status === 401) {
        // Token expired — bounce back to the unlock screen.
        setSecurityToken('');
      }
    } finally {
      setOverviewLoading(false);
    }
  };

  // Load the overview once we've got a security token.
  useEffect(() => {
    if (unlocked && securityToken) loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlocked, securityToken]);

  const clearTimeout = async () => {
    if (!securityToken) return;
    setClearing(true);
    try {
      await api.post(`/admin/users/${targetUser.id}/security/clear-timeout`, null, { params: { token: securityToken } });
      toast.success('Timeout cleared');
      await loadOverview();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to clear timeout';
      toast.error(detail);
    } finally {
      setClearing(false);
    }
  };

  // Gated re-auth screen.
  if (!unlocked || !securityToken) {
    return (
      <div className="space-y-4" data-testid="security-tab-locked">
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wider mb-1 flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Re-authentication Required
          </p>
          <p className="text-xs text-amber-700 dark:text-amber-400">
            For your security, re-enter your password{needsTotp ? ' and authenticator code' : ''} to view this user's security data.
          </p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs flex items-center gap-1">
            <Lock className="w-3 h-3" /> Your Password
          </Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your admin password"
            data-testid="security-verify-password"
            onKeyDown={(e) => { if (e.key === 'Enter') verify(); }}
          />
        </div>
        {needsTotp && (
          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1">
              <Shield className="w-3 h-3" /> Authenticator Code
            </Label>
            <Input
              type="text"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="6-digit code"
              maxLength={6}
              className="font-mono tracking-widest"
              data-testid="security-verify-totp"
              onKeyDown={(e) => { if (e.key === 'Enter') verify(); }}
            />
          </div>
        )}
        {verifyError && (
          <p className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded" data-testid="security-verify-error">{verifyError}</p>
        )}
        <Button
          className="w-full bg-[#586D52] hover:bg-[#475a43]"
          onClick={verify}
          disabled={verifying || !password || (needsTotp && totpCode.length < 6)}
          data-testid="security-verify-submit"
        >
          {verifying ? 'Verifying…' : 'Unlock Security Tab'}
        </Button>
      </div>
    );
  }

  // Unlocked view.
  const la = overview?.login_attempts || {};
  const ca = overview?.captcha_attempts || {};
  const to = overview?.timeout || {};
  const sessionRemaining = tokenExpiresAt ? Math.max(0, Math.floor((tokenExpiresAt - now) / 1000)) : 0;
  const lockedRemaining = to.remaining_seconds || 0;

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';
  const fmtCountdown = (s) => {
    if (!s) return '0s';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  return (
    <div className="space-y-4" data-testid="security-tab-unlocked">
      {/* Step2d-bug label — tiny indicator if this user is in the
          stuck-on-MFA-due-to-old-label population. Disappears
          automatically once they're back-filled or reset. */}
      <Step2dBugLabel api={api} userId={targetUser.id} />

      <div className="flex items-center justify-between rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-3 py-2">
        <p className="text-[11px] text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
          <Shield className="w-3 h-3" /> Unlocked · session expires in {fmtCountdown(sessionRemaining)}
        </p>
        <button
          onClick={loadOverview}
          className="text-[11px] text-emerald-700 dark:text-emerald-300 underline"
          data-testid="security-refresh"
        >
          Refresh
        </button>
      </div>

      {overviewLoading ? (
        <div className="py-6 text-center text-xs text-muted-foreground">Loading…</div>
      ) : (
        <>
          {/* Attempts */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-2" data-testid="security-attempts">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Attempts</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-muted-foreground">Failed logins (total):</span> <span className="font-mono font-semibold">{la.failed_login_attempts_total ?? 0}</span></div>
              <div><span className="text-muted-foreground">In current window:</span> <span className="font-mono font-semibold">{la.attempts_in_window ?? 0}</span></div>
              <div><span className="text-muted-foreground">Last failed:</span> <span className="font-medium">{fmtDate(la.last_failed_at)}</span></div>
              <div><span className="text-muted-foreground">Last success:</span> <span className="font-medium">{fmtDate(la.last_success_at)}</span></div>
              <div className="col-span-2 border-t border-gray-200 dark:border-gray-700 mt-1 pt-2"></div>
              <div><span className="text-muted-foreground">Captcha level:</span> <span className="font-mono font-semibold">{ca.level ?? 0}</span></div>
              <div><span className="text-muted-foreground">Captcha attempts:</span> <span className="font-mono font-semibold">{ca.attempts_in_level ?? 0}</span></div>
              <div className="col-span-2"><span className="text-muted-foreground">Last captcha block:</span> <span className="font-medium">{fmtDate(ca.last_block_at)}</span></div>
            </div>
          </div>

          {/* Timeouts */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3" data-testid="security-timeouts">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Timeouts</p>
            {to.active ? (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 space-y-1">
                <p className="text-sm font-semibold text-red-700 dark:text-red-300">User is currently timed out</p>
                <p className="text-xs text-red-600 dark:text-red-400">Lockout expires in <span className="font-mono">{fmtCountdown(lockedRemaining)}</span> (escalation level {to.lockout_level ?? 0})</p>
                <p className="text-[11px] text-red-600 dark:text-red-400">Expires at: {fmtDate(to.locked_until)}</p>
              </div>
            ) : ca.block_active ? (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 space-y-1">
                <p className="text-sm font-semibold text-red-700 dark:text-red-300">User is signup-captcha-blocked</p>
                <p className="text-xs text-red-600 dark:text-red-400">Block expires in <span className="font-mono">{fmtCountdown(ca.block_remaining_seconds || 0)}</span></p>
              </div>
            ) : (
              <div className="rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3">
                <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">No active timeout</p>
                <p className="text-[11px] text-emerald-700 dark:text-emerald-400">User can sign in normally.</p>
              </div>
            )}
            <Button
              variant="outline"
              className="w-full border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20"
              onClick={clearTimeout}
              disabled={clearing || (!to.active && !ca.block_active && (la.attempts_in_window ?? 0) === 0 && (ca.attempts_in_level ?? 0) === 0)}
              data-testid="security-clear-timeout"
            >
              {clearing ? 'Clearing…' : 'Clear Timeout'}
            </Button>
            <p className="text-[10px] text-muted-foreground">
              Resets the user's brute-force counters and captcha-email block. The user can attempt to sign in immediately after.
              {to.is_locked_flag ? ' Note: this user is also manually locked (Metrics tab) — that override is NOT touched here.' : ''}
            </p>
          </div>

          {/* Reset MFA — admin can wipe authenticator + recovery key.
              The user is force-stepped through setup on their next
              session via the non-dismissible PersistentMfaSetup modal
              (read by Layout / App from `user.mfa_setup_required`). */}
          {!isSelf && (
            <ResetMfaSection
              api={api}
              userId={targetUser.id}
              userName={`${targetUser.first_name || ''} ${targetUser.last_name || ''}`.trim()}
              securityToken={securityToken}
            />
          )}

          {/* Force rotations — distinct from Reset MFA. User keeps
              their current credentials; modal prompts for old + new. */}
          {!isSelf && (
            <ForceRotationSection
              api={api}
              userId={targetUser.id}
              userName={`${targetUser.first_name || ''} ${targetUser.last_name || ''}`.trim()}
              securityToken={securityToken}
              targetUser={targetUser}
            />
          )}

          {/* Bypass — single-use override for users who forgot MFA
              and/or recovery key in addition to their password. */}
          {!isSelf && (
            <BypassSection
              api={api}
              userId={targetUser.id}
              securityToken={securityToken}
              overrideRequest={overrideRequestCtx}
            />
          )}

          {/* Reset Password (moved here from outside the tabs) */}
          {!isSelf && (
            <ResetPasswordSection
              api={api}
              userId={targetUser.id}
              userName={`${targetUser.first_name || ''} ${targetUser.last_name || ''}`.trim()}
            />
          )}
        </>
      )}
    </div>
  );
}

function LiveBypassCountdown({ initialSeconds, onExpire }) {
  const [remaining, setRemaining] = useState(initialSeconds);
  useEffect(() => { setRemaining(initialSeconds); }, [initialSeconds]);
  useEffect(() => {
    if (remaining <= 0) return;
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(t); if (onExpire) onExpire(); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [remaining > 0, onExpire]); // eslint-disable-line react-hooks/exhaustive-deps
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  return (
    <span className="text-[11px] font-mono text-amber-700 dark:text-amber-300" data-testid="bypass-countdown">
      expires in {mm}:{ss}
    </span>
  );
}

function BypassSection({ api, userId, securityToken, overrideRequest }) {
  const [active, setActive] = useState(null); // null=unknown, false=none, {...}=present
  const [bypassMfa, setBypassMfa] = useState(true);
  const [bypassRecovery, setBypassRecovery] = useState(false);
  const [pwd, setPwd] = useState('');
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notifyDialog, setNotifyDialog] = useState(null); // { bypass_id, override_request_id }
  // iter132 — Bypass history for the collapsible audit panel.
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  // When an override-request context is supplied (admin clicked
  // "Approve Bypass" from a notification), prefill the factor
  // checkboxes from the supervisor's request so the admin doesn't
  // have to remember which were requested.
  useEffect(() => {
    if (overrideRequest?.factors) {
      setBypassMfa(!!overrideRequest.factors.mfa);
      setBypassRecovery(!!overrideRequest.factors.recovery_key);
    }
  }, [overrideRequest?.id]); // eslint-disable-line

  const load = async () => {
    try {
      const res = await api.get(`/admin/users/${userId}/security/bypass`, { params: { token: securityToken } });
      setActive(res.data?.active ? res.data : false);
    } catch (e) {
      setActive(false);
    }
    // iter132 — Always refresh history alongside the active state.
    try {
      const h = await api.get(`/admin/users/${userId}/security/bypass/history`, { params: { token: securityToken } });
      setHistory(h.data?.history || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { if (securityToken) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [securityToken]);

  // iter132 — Poll while a bypass is active so the panel auto-clears
  // the moment the user consumes it (or it expires server-side).
  // Without this the admin would see a stale "Active Bypass" card
  // until they manually re-open the security tab.
  useEffect(() => {
    if (!securityToken || !active) return;
    const interval = setInterval(load, 8000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, securityToken]);

  const create = async () => {
    if (!bypassMfa && !bypassRecovery) { toast.error('Select at least one'); return; }
    if (!pwd) { toast.error('Override password required'); return; }
    setCreating(true);
    try {
      const res = await api.post(`/admin/users/${userId}/security/bypass`, {
        bypass_mfa: bypassMfa, bypass_recovery_key: bypassRecovery, override_password: pwd,
      }, { params: { token: securityToken } });
      const bypassId = res.data?.bypass_id || res.data?.id;
      setActive({ active: true, ...res.data });
      setPwd('');
      toast.success('Bypass created');
      // If this Issue Bypass was triggered from a supervisor's
      // override request, ask the admin whether to notify the
      // supervisor (which reveals the override password on the
      // supervisor's email-detail circle).
      if (overrideRequest?.id && bypassId) {
        setNotifyDialog({ bypass_id: bypassId, override_request_id: overrideRequest.id });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setCreating(false); }
  };

  const decideNotify = async (notify) => {
    const { bypass_id, override_request_id } = notifyDialog || {};
    setNotifyDialog(null);
    try {
      await api.post(
        `/admin/override-requests/${override_request_id}/approve`,
        { notify },
        { params: { bypass_id } },
      );
      toast.success(notify ? 'Supervisor notified' : 'Bypass approved (supervisor not notified)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Notify-supervisor step failed');
    }
  };

  const cancel = async () => {
    try {
      await api.delete(`/admin/users/${userId}/security/bypass`, { params: { token: securityToken } });
      setActive(false);
      toast.success('Bypass cancelled');
      // iter132 — Refresh history so the cancelled row shows up
      // immediately in the audit panel.
      load();
    } catch (e) { toast.error('Failed'); }
  };

  // iter132 — Renders the collapsible Bypass History panel.
  // Shared between the active-bypass card and the issue-bypass card
  // so the admin always has access to the audit trail.
  const HistoryPanel = () => {
    if (!historyOpen) {
      return (
        <button
          onClick={() => setHistoryOpen(true)}
          className="w-full text-[11px] text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 py-1.5"
          data-testid="bypass-history-toggle"
        >
          <ChevronDown className="w-3 h-3" /> Show Bypass History ({history.length})
        </button>
      );
    }
    return (
      <div className="space-y-1.5" data-testid="bypass-history-panel">
        <button
          onClick={() => setHistoryOpen(false)}
          className="w-full text-[11px] text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 py-1.5"
          data-testid="bypass-history-toggle"
        >
          <ChevronUp className="w-3 h-3" /> Hide Bypass History
        </button>
        {history.length === 0 ? (
          <p className="text-[11px] text-muted-foreground italic text-center py-2">No prior bypasses for this user.</p>
        ) : (
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {history.map((h) => {
              const statusColor = {
                active: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700/40',
                consumed: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700/40',
                expired: 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/40 border-gray-300 dark:border-gray-700',
                cancelled: 'text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/20 border-rose-300 dark:border-rose-700/40',
              }[h.status] || '';
              const factors = [
                h.bypass_mfa && 'MFA',
                h.bypass_recovery_key && 'Recovery',
              ].filter(Boolean).join(' + ') || '—';
              const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—';
              return (
                <div key={h.id} className={`rounded-md border px-2 py-1.5 text-[11px] ${statusColor}`} data-testid={`bypass-history-row-${h.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold uppercase tracking-wider">{h.status}</span>
                    <span className="font-mono text-[10px] opacity-75">{factors}</span>
                  </div>
                  <div className="flex flex-col gap-0.5 mt-0.5 opacity-90">
                    <span>Issued: {fmt(h.created_at)}</span>
                    {h.status === 'consumed' && <span>Consumed: {fmt(h.consumed_at)}</span>}
                    {h.status === 'expired' && <span>Expired: {fmt(h.expires_at)}</span>}
                    {h.status === 'cancelled' && <span>Cancelled: {fmt(h.cancelled_at)}</span>}
                    {h.status === 'active' && <span>Expires: {fmt(h.expires_at)}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (active) {
    return (
      <div className="rounded-xl border border-amber-300 dark:border-amber-700/40 bg-amber-50/60 dark:bg-amber-900/10 p-3 space-y-2" data-testid="bypass-active">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-900 dark:text-amber-200">Active Bypass</p>
          <LiveBypassCountdown initialSeconds={active.remaining_seconds || 0} onExpire={load} />
        </div>
        <div className="text-[11px] space-y-0.5 text-amber-800 dark:text-amber-300">
          {active.bypass_mfa && <p>• MFA bypassed</p>}
          {active.bypass_recovery_key && <p>• Recovery key bypassed</p>}
        </div>
        <div className="rounded-md bg-white/70 dark:bg-amber-900/20 p-2 flex items-center justify-between gap-2">
          <code className="font-mono text-xs font-bold break-all" data-testid="bypass-override-password">{active.override_password}</code>
          <button
            onClick={() => { navigator.clipboard.writeText(active.override_password); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            className="text-amber-700 dark:text-amber-300 shrink-0"
            data-testid="bypass-copy"
          >
            {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
        <Button variant="outline" size="sm" className="w-full h-7 text-xs" onClick={cancel} data-testid="bypass-cancel">Cancel Bypass</Button>
        <HistoryPanel />
      </div>
    );
  }

  return (
    <>
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-2" data-testid="bypass-section">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bypass</p>
      {overrideRequest && (
        <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/40 p-2 text-[11px] text-amber-900 dark:text-amber-200" data-testid="bypass-override-context-banner">
          Approving supervisor request from <span className="font-semibold">{overrideRequest.supervisor_name}</span> for <span className="font-semibold">{overrideRequest.requester_name}</span>. Factors prefilled.
        </div>
      )}
      <div className="flex gap-3 text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={bypassMfa} onChange={(e) => setBypassMfa(e.target.checked)} data-testid="bypass-mfa-checkbox" />
          MFA
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={bypassRecovery} onChange={(e) => setBypassRecovery(e.target.checked)} data-testid="bypass-recovery-checkbox" />
          Recovery Key
        </label>
      </div>
      <Input
        type="text"
        value={pwd}
        onChange={(e) => setPwd(e.target.value)}
        placeholder="Override password (admin policy)"
        className="font-mono text-xs"
        data-testid="bypass-pwd"
      />
      <Button
        size="sm"
        className="w-full h-7 text-xs bg-amber-700 hover:bg-amber-800 text-white"
        onClick={create}
        disabled={creating || !pwd || (!bypassMfa && !bypassRecovery)}
        data-testid="bypass-create"
      >
        {creating ? 'Creating…' : 'Issue Bypass (1h)'}
      </Button>
      <HistoryPanel />
    </div>
      {notifyDialog && (
        <Dialog open={true} onOpenChange={() => setNotifyDialog(null)}>
          <DialogContent className="max-w-sm" data-testid="bypass-notify-dialog">
            <DialogHeader>
              <DialogTitle>Notify Supervisor?</DialogTitle>
              <DialogDescription className="text-xs">
                The supervisor who requested this override can see the override password instantly on their email-detail circle if you notify them now.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => decideNotify(false)} data-testid="notify-supervisor-no">
                No
              </Button>
              <Button size="sm" className="bg-[#586D52] hover:bg-[#4a5d46]" onClick={() => decideNotify(true)} data-testid="notify-supervisor-yes">
                Yes — Notify
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function Step2dBugLabel({ api, userId }) {
  const [isBug, setIsBug] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get('/admin/onboarding/step2d-backfill-candidates').then((r) => {
      if (cancelled) return;
      setIsBug(!!(r.data?.users || []).find((u) => u.id === userId));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [api, userId]);
  if (!isBug) return null;
  return (
    <p className="text-[10px] text-amber-700 dark:text-amber-300 flex items-center gap-1" data-testid="step2d-bug-label">
      <AlertTriangle className="w-2.5 h-2.5" />
      MFA stuck (old label) — Reset MFA clears it.
    </p>
  );
}

function ForceRotationSection({ api, userId, userName, securityToken, targetUser }) {
  const [busyPwd, setBusyPwd] = useState(false);
  const [busyMfa, setBusyMfa] = useState(false);
  const [pwdPending, setPwdPending] = useState(!!targetUser?.password_rotation_required);
  const [mfaPending, setMfaPending] = useState(!!targetUser?.mfa_rotation_required);

  const forcePwd = async () => {
    setBusyPwd(true);
    try {
      await api.post(`/admin/users/${userId}/security/force-password-rotation`, null, { params: { token: securityToken } });
      setPwdPending(true);
      toast.success('Password rotation queued');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusyPwd(false); }
  };
  const forceMfa = async () => {
    if (!targetUser?.totp_enabled) { toast.error('User has no authenticator yet'); return; }
    setBusyMfa(true);
    try {
      await api.post(`/admin/users/${userId}/security/force-mfa-rotation`, null, { params: { token: securityToken } });
      setMfaPending(true);
      toast.success('MFA rotation queued');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusyMfa(false); }
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-2" data-testid="force-rotation-section">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Force Rotation</p>
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant="outline"
          className="h-8 text-xs"
          onClick={forcePwd}
          disabled={busyPwd || pwdPending}
          data-testid="force-pwd-rotation"
        >
          {pwdPending ? 'Pwd queued' : 'Force Password'}
        </Button>
        <Button
          variant="outline"
          className="h-8 text-xs"
          onClick={forceMfa}
          disabled={busyMfa || mfaPending}
          data-testid="force-mfa-rotation"
        >
          {mfaPending ? 'MFA queued' : 'Force MFA'}
        </Button>
      </div>
    </div>
  );
}

function ResetMfaSection({ api, userId, userName, securityToken }) {
  const [open, setOpen] = useState(false);
  const [resetTotp, setResetTotp] = useState(true);
  const [resetRecoveryKey, setResetRecoveryKey] = useState(true);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [confirmText, setConfirmText] = useState('');

  const expectedConfirm = `RESET ${userName.split(' ')[0] || 'USER'}`;

  const submit = async () => {
    if (confirmText.trim().toUpperCase() !== expectedConfirm.toUpperCase()) {
      toast.error(`Type "${expectedConfirm}" to confirm`);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(
        `/admin/users/${userId}/security/reset-mfa`,
        { reset_totp: resetTotp, reset_recovery_key: resetRecoveryKey },
        { params: { token: securityToken } }
      );
      setDone(res.data);
      toast.success(`MFA reset (${(res.data.required || []).join(', ')})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reset MFA');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2" data-testid="reset-mfa-done">
        <p className="text-xs font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300">MFA Reset</p>
        <p className="text-xs text-amber-700 dark:text-amber-300">
          Reset {(done.required || []).join(' + ')} for {userName || 'user'}. On their next session they'll be locked into a setup modal until they re-enroll.
        </p>
        <p className="text-[10px] text-amber-700/80 dark:text-amber-300/80">Reset at {new Date(done.reset_at).toLocaleString()}</p>
        <button
          className="text-[11px] underline text-amber-700 dark:text-amber-300"
          onClick={() => { setDone(null); setOpen(false); setConfirmText(''); }}
          data-testid="reset-mfa-done-close"
        >
          Done
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-2" data-testid="reset-mfa-collapsed">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reset MFA</p>
        <p className="text-[11px] text-muted-foreground">
          Force {userName || 'this user'} to re-set up Authenticator and/or Recovery Key. They'll be locked into a non-dismissible setup modal on next session.
        </p>
        <Button
          variant="outline"
          className="w-full border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/20"
          onClick={() => setOpen(true)}
          data-testid="open-reset-mfa"
        >
          Reset MFA…
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-red-300 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 p-3 space-y-3" data-testid="reset-mfa-open">
      <p className="text-xs font-semibold uppercase tracking-wider text-red-800 dark:text-red-300">Reset MFA — Destructive</p>
      <div className="space-y-2 text-xs">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={resetTotp}
            onChange={(e) => setResetTotp(e.target.checked)}
            data-testid="reset-mfa-totp"
          />
          <span>Reset Authenticator App (TOTP)</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={resetRecoveryKey}
            onChange={(e) => setResetRecoveryKey(e.target.checked)}
            data-testid="reset-mfa-recovery"
          />
          <span>Reset Recovery Key</span>
        </label>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Type <span className="font-mono font-semibold">{expectedConfirm}</span> to confirm</Label>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={expectedConfirm}
          data-testid="reset-mfa-confirm"
        />
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => { setOpen(false); setConfirmText(''); }}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
          onClick={submit}
          disabled={busy || (!resetTotp && !resetRecoveryKey)}
          data-testid="reset-mfa-submit"
        >
          {busy ? 'Resetting…' : 'Reset MFA'}
        </Button>
      </div>
      <p className="text-[10px] text-red-700/80 dark:text-red-300/80">
        On the user's next session they'll see a non-dismissible setup modal that persists across tab close / sign-out until they finish enrolment.
      </p>
    </div>
  );
}

function ResetPasswordSection({ api, userId, userName }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('generate'); // 'generate' | 'custom'
  const [customPw, setCustomPw] = useState('');
  const [revealedPw, setRevealedPw] = useState(null);
  const [resetAt, setResetAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const body = mode === 'generate' ? { generate: true } : { password: customPw };
      const res = await api.post(`/admin/users/${userId}/reset-password`, body);
      setRevealedPw(res.data?.new_password);
      setResetAt(res.data?.reset_at);
      setCustomPw('');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not reset password';
      window.alert(msg);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!revealedPw) return;
    try {
      await navigator.clipboard.writeText(revealedPw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* ignore */ }
  };

  const close = () => {
    // Wipe in-memory copy. After close, the password is unrecoverable from the UI.
    setRevealedPw(null);
    setResetAt(null);
    setCustomPw('');
    setMode('generate');
    setOpen(false);
  };

  if (!open) {
    return (
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="w-full text-left text-sm font-medium text-red-600 hover:text-red-700 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/10"
          data-testid="open-reset-password"
        >
          Reset Password…
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3" data-testid="reset-password-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-red-600">Reset Password{userName ? ` for ${userName}` : ''}</p>
        <button onClick={close} className="text-xs text-muted-foreground hover:underline">Close</button>
      </div>

      {revealedPw ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/10 p-3 space-y-2">
          <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">New password — copy now. You will not see it again.</p>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={revealedPw}
              className="flex-1 h-9 px-2 rounded-lg border bg-white dark:bg-[#0d0d0d] text-sm font-mono"
              data-testid="reset-password-result"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={copy}
              className="h-9 px-3 rounded-lg bg-[#586D52] text-white text-xs font-semibold"
              data-testid="reset-password-copy"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {resetAt && <p className="text-[10px] text-muted-foreground">Set at {new Date(resetAt).toLocaleString()}</p>}
          <p className="text-[11px] text-amber-800 dark:text-amber-400">Tip: send this to the user via a secure channel. Reopening the dialog will hide it.</p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 text-xs">
            <button
              onClick={() => setMode('generate')}
              className={`flex-1 px-3 py-2 rounded-lg border ${mode === 'generate' ? 'bg-[#586D52]/10 border-[#586D52] text-[#586D52]' : 'bg-background'}`}
              data-testid="reset-mode-generate"
            >
              Generate Random
            </button>
            <button
              onClick={() => setMode('custom')}
              className={`flex-1 px-3 py-2 rounded-lg border ${mode === 'custom' ? 'bg-[#586D52]/10 border-[#586D52] text-[#586D52]' : 'bg-background'}`}
              data-testid="reset-mode-custom"
            >
              Enter Custom
            </button>
          </div>
          {mode === 'custom' && (
            <div className="space-y-1">
              <input
                type="text"
                value={customPw}
                onChange={(e) => setCustomPw(e.target.value)}
                placeholder="Type the new password"
                className="w-full h-9 px-2 rounded-lg border bg-background text-sm"
                style={{ fontSize: '16px' }}
                data-testid="reset-password-input"
                autoComplete="off"
              />
              {/* iter163 — Mirror the EXACT backend rules
                  (security_signup.py::assert_strong_password). Each
                  line turns green ✓ once that rule is satisfied so
                  the admin gets live, unambiguous feedback. */}
              {(() => {
                const v = customPw || '';
                const rules = [
                  { ok: v.length >= 12, label: 'At least 12 characters' },
                  { ok: /[A-Z]/.test(v), label: 'At least one uppercase letter (A-Z)' },
                  { ok: /[0-9]/.test(v), label: 'At least one digit (0-9)' },
                  { ok: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(v), label: 'At least one special character (! @ # $ % & etc.)' },
                ];
                return (
                  <ul className="text-[11px] mt-1 space-y-0.5" data-testid="reset-password-rules">
                    {rules.map((r, i) => (
                      <li
                        key={i}
                        className={r.ok ? 'text-green-600' : 'text-muted-foreground'}
                      >
                        {r.ok ? '✓' : '○'} {r.label}
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          )}
          <button
            onClick={submit}
            disabled={(() => {
              if (busy) return true;
              if (mode !== 'custom') return false;
              const v = customPw || '';
              return !(
                v.length >= 12 &&
                /[A-Z]/.test(v) &&
                /[0-9]/.test(v) &&
                /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(v)
              );
            })()}
            className="w-full h-9 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
            data-testid="reset-password-submit"
          >
            {busy ? 'Setting…' : (mode === 'generate' ? 'Generate & Save' : 'Save New Password')}
          </button>
          <p className="text-[11px] text-muted-foreground">
            For security, existing passwords are never visible. Resetting overwrites the user's current password immediately.
          </p>
        </>
      )}
    </div>
  );
}
